import { useRef, useState } from "react";
import type {
  CaseRecord,
  FindingActionKind,
  ValidationIssue,
} from "../../types";
import {
  buildCitationNumbering,
  citedRecordIds,
  indexRecords,
} from "../../lib/provenance";
import FindingsProse from "./FindingsProse";

// Side-by-side provenance view (the "View sources" mode). LEFT: the
// AI-generated content with numbered citation badges and uncited-claim
// underlines. RIGHT: the cited source records, each with its matching
// badge number, date, sender, subject and excerpt. A draggable vertical
// handle resizes the panels; the ratio persists in localStorage.
//
// Interactions:
//   - Click a citation badge (left) → scroll the right panel to that
//     record and highlight it.
//   - Click a record (right) → highlight every claim citing it (left).

const RATIO_KEY = "casethread:provenance-split-ratio";
const MIN_PX = 250;

function loadRatio(): number {
  try {
    const v = parseFloat(window.localStorage.getItem(RATIO_KEY) ?? "");
    return Number.isFinite(v) && v > 0.1 && v < 0.9 ? v : 0.5;
  } catch {
    return 0.5;
  }
}

export default function ProvenanceView({
  text,
  records,
  onNavigateToRecord,
  findings = [],
  actions = {},
  notes = {},
  onAction = () => {},
}: {
  text: string;
  records: CaseRecord[];
  onNavigateToRecord?: (recordId: string) => void;
  // Findings so warning markers render in the source view too.
  findings?: ValidationIssue[];
  actions?: Record<string, FindingActionKind>;
  notes?: Record<string, string>;
  onAction?: (
    findingId: string,
    kind: FindingActionKind,
    note?: string,
  ) => void;
}) {
  const [ratio, setRatio] = useState<number>(loadRatio);
  const [activeRecordId, setActiveRecordId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const recordRefs = useRef<Record<string, HTMLLIElement | null>>({});

  const numbering = buildCitationNumbering(text);
  const recordIndex = indexRecords(records);
  const citedIds = citedRecordIds(text);

  // Clicking a badge: highlight + scroll the matching record into view.
  function focusRecord(id: string) {
    setActiveRecordId(id);
    recordRefs.current[id]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }

  function startDrag(e: React.PointerEvent) {
    e.preventDefault();
    const container = containerRef.current;
    if (!container) return;
    const onMove = (ev: PointerEvent) => {
      const rect = container.getBoundingClientRect();
      if (rect.width < MIN_PX * 2) return;
      let r = (ev.clientX - rect.left) / rect.width;
      const minR = MIN_PX / rect.width;
      r = Math.max(minR, Math.min(1 - minR, r));
      setRatio(r);
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      try {
        window.localStorage.setItem(RATIO_KEY, String(ratioRef.current));
      } catch {
        /* ignore */
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }

  // Keep the latest ratio available to the pointerup handler without
  // re-binding listeners on every move.
  const ratioRef = useRef(ratio);
  ratioRef.current = ratio;

  return (
    <div className="prov-view">
      <div className="prov-split" ref={containerRef}>
        <div className="prov-pane prov-left" style={{ flex: `${ratio} 1 0` }}>
          <FindingsProse
            text={text}
            records={records}
            numbering={numbering}
            findings={findings}
            actions={actions}
            notes={notes}
            onAction={onAction}
            onCite={focusRecord}
            activeRecordId={activeRecordId}
          />
        </div>

        <div
          className="prov-divider"
          role="separator"
          aria-orientation="vertical"
          onPointerDown={startDrag}
          title="Drag to resize"
        />

        <div
          className="prov-pane prov-right"
          style={{ flex: `${1 - ratio} 1 0` }}
        >
          {citedIds.length === 0 ? (
            <p className="muted prov-empty">No source citations in this content.</p>
          ) : (
            <ol className="prov-records">
              {citedIds.map((id) => {
                const rec = recordIndex.get(id);
                const n = numbering.get(id) ?? 0;
                return (
                  <li
                    key={id}
                    ref={(el) => {
                      recordRefs.current[id] = el;
                    }}
                    className={`prov-record${activeRecordId === id ? " active" : ""}`}
                    onClick={() => setActiveRecordId(id)}
                  >
                    <div className="prov-record-head">
                      <span className="cite-ref-static">{n}</span>
                      <span className="prov-record-meta">
                        {rec?.date ?? "unknown date"}
                        {rec?.sender ? ` · ${rec.sender}` : ""}
                      </span>
                      {onNavigateToRecord && rec && (
                        <button
                          type="button"
                          className="prov-record-open"
                          onClick={(e) => {
                            e.stopPropagation();
                            onNavigateToRecord(id);
                          }}
                        >
                          Open ↗
                        </button>
                      )}
                    </div>
                    {(rec?.label || rec?.subject) && (
                      <div
                        className="prov-record-subject"
                        title={rec?.label ? rec?.subject ?? undefined : undefined}
                      >
                        {rec?.label || rec?.subject}
                      </div>
                    )}
                    <div className="prov-record-excerpt">
                      {rec?.excerpt || "(no text)"}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>

      <div className="prov-legend">
        <span className="prov-legend-item">
          <span className="cite-ref-static">1</span> source reference
        </span>
        <span className="prov-legend-item">
          <span className="prov-uncited prov-legend-swatch">claim</span> no
          source citation
        </span>
      </div>
    </div>
  );
}
